/**
 * Whether a pasted URL is a LinkedIn company page, and which company it names (PREC-4461).
 *
 * A company-activity source stores the page it scrapes followers from and the run reads the id
 * with `url.split("/company/")[1].split("/")[0]`. Nothing validated the shape, so a profile
 * (`/in/…`), a school, a showcase page or a typo threw a TypeError before the scrape began and
 * the list ended with the generic "Run failed". The form, the API DTO and the run must agree on
 * one answer, so the parser is pure and lives here for both apps ([CLAUDE.md] "Where to put new
 * code").
 *
 * A LinkedIn company page is a `linkedin.com` (or subdomain) URL whose first path segment is
 * `company`; the segment after it is the numeric id or the slug. Everything else is not one.
 */

const LINKEDIN_HOST = /^(?:[a-z0-9-]+\.)*linkedin\.com$/i;

/** The whole first path segment after `/company/`, or `null` when it names none. */
const COMPANY_PATH = /^\/company\/([^/]+)(?:\/.*)?$/i;

function parseLinkedinUrl(value: string | null | undefined): URL | null {
	if (!value) {
		return null;
	}

	const trimmed = value.trim();

	if (!trimmed) {
		return null;
	}

	// A browser copy can arrive without the scheme; a bare host is still unambiguous.
	const candidate = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;

	try {
		const url = new URL(candidate);

		return (url.protocol === "https:" || url.protocol === "http:") &&
			LINKEDIN_HOST.test(url.hostname)
			? url
			: null;
	} catch {
		return null;
	}
}

/**
 * The company id/slug a LinkedIn company page URL names, or `null` for anything that is not one
 * (a profile, a school, a showcase page, another host, an empty or unreadable value). The query
 * string and hash are ignored, so a copied page URL with tracking parameters still parses.
 */
export function parseLinkedinCompanyId(url: string | null | undefined): string | null {
	const parsed = parseLinkedinUrl(url);

	if (!parsed) {
		return null;
	}

	const match = COMPANY_PATH.exec(parsed.pathname);

	return match?.[1] ? match[1] : null;
}

/** Whether `url` is a LinkedIn company page URL. */
export function isLinkedinCompanyUrl(url: string | null | undefined): boolean {
	return parseLinkedinCompanyId(url) !== null;
}
